import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { IAssembly, ILayout } from 'src/interfaces';
import { queryKeys, requestCCApi } from 'src/utils';

import { mapAssembly } from '../assembly/helpers';
import { fetchLayoutType } from '../layoutTypes';
import { fetchAssembliesWithoutPagination } from './useGetAssemblies';

export const useGetAssembly = (assemblyId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.assemblies.details(assemblyId),
    queryFn: () => fetchAssembly(assemblyId),
    enabled: Boolean(assemblyId) && enabled,
    staleTime: Infinity,
  });

const fetchAssembly = async (assemblyId: string) => {
  const [response, layoutTypes] = await Promise.all([
    requestCCApi({
      url: `/assemblies/${assemblyId}?include=layouts`,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
      staleTime: Infinity,
    }),
  ]);
  const layouts: ILayout[] = get(response, 'includes.layouts.data', []);
  const layout = layouts.find((item) => item.id === response?.data?.layout_id);
  if (layout) {
    layout.layoutType = layoutTypes.find(
      (item) => item.id === layout.layout_type_id,
    );
  }

  let parentAssembly: IAssembly | undefined;
  let childrenAssemblies: IAssembly[] = [];
  let siblingAssemblies: IAssembly[] = [];
  const isParentAssembly = !response.data.variation_parent_id;

  if (isParentAssembly) {
    childrenAssemblies = await queryClient.fetchQuery({
      queryKey: queryKeys.assemblies.all(
        `variation_parent_id=${response.data.id}`,
      ),
      queryFn: () =>
        fetchAssembliesWithoutPagination([
          `variation_parent_id=${response.data.id}`,
        ]),
      staleTime: Infinity,
    });
  } else {
    [siblingAssemblies, parentAssembly] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: queryKeys.assemblies.all(
          `variation_parent_id=${response.data.variation_parent_id}`,
        ),
        queryFn: () =>
          fetchAssembliesWithoutPagination([
            `variation_parent_id=${response.data.variation_parent_id}`,
          ]),
        staleTime: Infinity,
      }),
      queryClient.fetchQuery({
        queryKey: queryKeys.assemblies.details(
          response.data.variation_parent_id,
        ),
        queryFn: () => fetchAssembly(response.data.variation_parent_id),
        staleTime: Infinity,
      }),
    ]);
  }

  return mapAssembly({
    assembly: response.data,
    childrenAssemblies: childrenAssemblies,
    siblingAssemblies: siblingAssemblies,
    parentAssembly: parentAssembly,
    layout: layout,
  });
};
